/**
 * Theme preference of the app (F8 D-2). The key and the values are a contract shared with
 * `apps/app/public/theme-init.js` (runs before the bundle) and the site's `public/theme.js`:
 * `localStorage['dcs.theme']` holds `system`, `light` or `dark`. Anything else (or no storage)
 * means "follow the system".
 */
export const THEME_STORAGE_KEY = 'dcs.theme';

export const THEME_PREFERENCES = ['system', 'light', 'dark'] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];

/** The side the page is drawn in once the preference is applied. */
export type ResolvedTheme = 'light' | 'dark';

/** Media query that tells whether the system asks for dark. */
export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/**
 * `<meta name="theme-color">` per side (browser chrome, PWA title bar). Light: `--color-primary`
 * (as `index.html` and the manifest), dark: `--color-bg` of `@dcs/ui/tokens.css` (checked by
 * theme.test.ts).
 */
export const THEME_COLORS: Readonly<Record<ResolvedTheme, string>> = {
  light: '#1d4ed8',
  dark: '#0b1220',
};

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value);
}

/** A stored value as a preference; unknown or missing values fall back to `system`. */
export function parseThemePreference(value: string | null): ThemePreference {
  return isThemePreference(value) ? value : 'system';
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}
